// Project publication (decision D31 "Published app is the project's preview"): the project header shows whether the app
// was published to Coolify and where (GET /v1/projects/:id/publish), and its primary action follows the project's
// state — "Open app" once a published URL exists, "Publish" for an organization admin once every task is done, else
// "Open preview". The Overview "Application preview" card and the "Preview & tests" tab show the live URL when the
// project is published but has no preview target yet (a publish finished before D31), and otherwise say it is not
// published, offering the same Publish action to admins. Every refusal shows the API's own message inline (ActionError,
// role=alert) right where the Publish button is — never a silent spinner — with a "Connect GitHub" link when the fix is
// in Integrations (D33: a project without a repository is published by creating one, which needs the organization's
// GitHub connection), and a failed publish shows its stored error under the header's action row.
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useI18n } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import { FINAL_PUBLISH_STATES, getProjectPublish, publishProject, type ProjectPublish } from "../../api/endpoints-microsystem.ts";
import type { PreviewTarget } from "../../api/types-wave3.ts";
import { isApiError } from "../../api/client.ts";
import { Button } from "../../components/Button.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { EmptyState } from "../../components/Feedback.tsx";
import { Pill } from "../../components/Status.tsx";
import { useToast } from "../../components/Toast.tsx";
import { ExternalLinkIcon, MonitorIcon, UploadIcon } from "../../components/icons.tsx";
import { PUBLISH_POLL_MS, safeHttpUrl, usePolled } from "./LaunchCard.tsx";

/** Embed reason of the preview target the publish worker registers for a published app (D31). */
export const PUBLISHED_APP_REASON = "published_app";

export interface Publication {
  /** The latest publish; null when the project was never published; undefined until the first answer. */
  publish: ProjectPublish | null | undefined;
  error: unknown;
  reload: () => void;
}

/** Refusal reasons of POST /publish (D33) whose fix is in Integrations: connect, keep one, or allow the GitHub connection. */
const CONNECT_GITHUB_REASONS: ReadonlySet<string> = new Set(["no_github_integration", "github_integration_ambiguous", "github_not_allowlisted"]);
/** Error codes of a failed publish whose fix is in Integrations. */
const CONNECT_GITHUB_CODES: ReadonlySet<string> = new Set(["no_github_integration", "github_unavailable"]);

/** The fix of a refused publish request, when the UI can link to it ("connect_github" → Integrations). */
export function publishFixAction(error: unknown): "connect_github" | null {
  if (!isApiError(error)) return null;
  const d = error.details ?? {};
  if (d["action"] === "connect_github") return "connect_github";
  return typeof d["reason"] === "string" && CONNECT_GITHUB_REASONS.has(d["reason"]) ? "connect_github" : null;
}

function ConnectGithubLink() {
  const { t } = useI18n();
  return (
    <Link to="/integrations" className="btn btn--sm" data-testid="publish-connect-github">
      {t("publication.connectGithub")}
    </Link>
  );
}

/** A refused publish request: the API's message inline (role=alert), with the link to the fix when there is one. */
export function PublishRefusal({ error, onDismiss }: { error: unknown; onDismiss: () => void }) {
  if (!error) return null;
  return (
    <div className="publish-refusal" data-testid="publish-refusal">
      <ActionError error={error} onDismiss={onDismiss} extra={publishFixAction(error) ? <p><ConnectGithubLink /></p> : null} />
    </div>
  );
}

/** The stored failure of the latest publish (its own sentence and code), with the link to the fix when there is one. */
export function PublishFailure({ publish }: { publish: ProjectPublish | null | undefined }) {
  const { t } = useI18n();
  if (publish?.state !== "failed") return null;
  return (
    <div className="publish-failure" role="status" data-testid="publish-failure">
      <p className="launch-error">
        <span>{t("launch.publishFailed")}</span>
        {publish.error ? <span className="user-content"> {publish.error}</span> : null}
        {publish.error_code ? <span className="mono subtle"> ({publish.error_code})</span> : null}
      </p>
      {publish.error_code && CONNECT_GITHUB_CODES.has(publish.error_code) ? <ConnectGithubLink /> : null}
    </div>
  );
}

/** The project's latest publish, polled every PUBLISH_POLL_MS while one is in flight. */
export function useProjectPublication(projectId: string): Publication {
  const polled = usePolled(
    (s) => getProjectPublish(projectId, s).then((r) => r.publish),
    PUBLISH_POLL_MS,
    (p) => p !== null && !FINAL_PUBLISH_STATES.includes(p.state),
    [projectId],
  );
  return { publish: polled.value, error: polled.error, reload: polled.reload };
}

/** What the preview surfaces need to show the publication in place of an empty state (passed down by the project page). */
export interface PublicationContext {
  publication: Publication;
  isOrgAdmin: boolean;
  tasksDone: boolean;
}

export const publishInFlight = (publish: ProjectPublish | null | undefined): boolean => !!publish && !FINAL_PUBLISH_STATES.includes(publish.state);

/**
 * The project's live URL (safe http(s) only): the latest publish's app URL, else the preview target a publish
 * registered (the app stays live while a later publish runs or after it failed).
 */
export function liveAppUrl(publish: ProjectPublish | null | undefined, targets: readonly PreviewTarget[] | undefined): string | null {
  const fromPublish = publish?.state === "succeeded" ? safeHttpUrl(publish.app_url) : null;
  if (fromPublish) return fromPublish;
  const published = (targets ?? []).find((t) => t.embed.reason === PUBLISHED_APP_REASON);
  return safeHttpUrl(published?.url ?? null);
}

/** "Not published" / "Publishing… (<state>)" / "Live" + URL / "Publish failed (<code>)", next to the project state. */
export function PublicationStatus({ publish }: { publish: ProjectPublish | null | undefined }) {
  const { t } = useI18n();
  if (publish === undefined) return null;
  if (publish === null) {
    return (
      <span className="row" data-testid="publication-status" data-state="none">
        <Pill tone="neutral" icon="circle">
          {t("publication.notPublished")}
        </Pill>
      </span>
    );
  }
  if (publishInFlight(publish)) {
    return (
      <span className="row" data-testid="publication-status" data-state={publish.state}>
        <Pill tone="info" icon="spinner">
          {t("publication.publishing", { state: t(`launch.publishState.${publish.state}` as MessageKey) })}
        </Pill>
      </span>
    );
  }
  if (publish.state === "failed") {
    return (
      <span className="row" data-testid="publication-status" data-state="failed">
        <Pill tone="danger" icon="x">
          {publish.error_code ? t("publication.failedCode", { code: publish.error_code }) : t("publication.failed")}
        </Pill>
      </span>
    );
  }
  const url = safeHttpUrl(publish.app_url);
  return (
    <span className="row row--wrap" style={{ gap: 8 }} data-testid="publication-status" data-state="succeeded">
      <Pill tone="success" icon="check">
        {t("publication.live")}
      </Pill>
      {url ? (
        <a className="link-subtle row publication-url" href={url} target="_blank" rel="noopener noreferrer" data-testid="publication-url">
          <span className="mono">{url}</span>
          <ExternalLinkIcon size={14} />
          <span className="sr-only">{t("common.opensNewTab")}</span>
        </a>
      ) : null}
    </span>
  );
}

/**
 * Starts a publish (organization admin; the API enforces it). Toast on success, the API's refusal otherwise — shown
 * through `onError` when given (the header renders it under itself), else right under the button.
 */
export function PublishButton({
  projectId,
  busy,
  onPublished,
  onError,
  variant = "primary",
  size = "md",
  testId,
  publishFn = publishProject,
}: {
  projectId: string;
  busy: boolean;
  onPublished: () => void;
  onError?: (error: unknown) => void;
  variant?: "primary" | "secondary";
  size?: "sm" | "md";
  testId: string;
  publishFn?: (projectId: string) => Promise<unknown>;
}) {
  const { t } = useI18n();
  const { toast } = useToast();
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const report = (e: unknown) => (onError ? onError(e) : setError(e));
  const run = async () => {
    setPublishing(true);
    report(null);
    try {
      await publishFn(projectId);
      toast(t("launch.publishRequested"), "success");
      onPublished();
    } catch (e) {
      report(e);
    } finally {
      setPublishing(false);
    }
  };
  return (
    <>
      <Button variant={variant} size={size} icon={<UploadIcon size={size === "sm" ? 14 : 16} />} loading={publishing} disabled={busy} onClick={() => void run()} data-testid={testId}>
        {t("publication.publish")}
      </Button>
      {onError ? null : <PublishRefusal error={error} onDismiss={() => setError(null)} />}
    </>
  );
}

/**
 * The header's actions after the execution controls, on one row: "Open app" (a published URL exists), else "Publish"
 * (organization admin, every task done), and "Open preview" — primary only when it is the main action and a preview
 * target exists; without a preview target it is secondary, its hint a tooltip and the line `hintId` names (rendered by
 * ProjectHeaderActions below the whole row, never under the button).
 */
export function ProjectPrimaryActions({
  projectId,
  publication,
  previewTargets,
  isOrgAdmin,
  tasksDone,
  onPublished,
  onPublishError,
  publishFn,
  hintId,
}: {
  projectId: string;
  publication: Publication;
  /** Undefined while loading (no hint is shown until known). */
  previewTargets: readonly PreviewTarget[] | undefined;
  isOrgAdmin: boolean;
  tasksDone: boolean;
  onPublished: () => void;
  onPublishError: (error: unknown) => void;
  publishFn?: (projectId: string) => Promise<unknown>;
  /** Id of the hint line rendered below the action row (aria-describedby of Open preview without a target). */
  hintId?: string;
}) {
  const { t } = useI18n();
  const appUrl = liveAppUrl(publication.publish, previewTargets);
  const showPublish = appUrl === null && isOrgAdmin && tasksDone;
  const noTarget = previewTargets !== undefined && previewTargets.length === 0;
  const previewPrimary = appUrl === null && !showPublish && !noTarget;
  return (
    <>
      {appUrl ? (
        <a className="btn btn--primary" href={appUrl} target="_blank" rel="noopener noreferrer" data-testid="open-app">
          <ExternalLinkIcon size={16} />
          {t("publication.openApp")}
          <span className="sr-only">{t("common.opensNewTab")}</span>
        </a>
      ) : null}
      {showPublish ? (
        <PublishButton projectId={projectId} busy={publishInFlight(publication.publish)} onPublished={onPublished} onError={onPublishError} testId="header-publish" {...(publishFn ? { publishFn } : {})} />
      ) : null}
      <Link
        to={`/projects/${projectId}/preview`}
        className={previewPrimary ? "btn btn--primary" : "btn"}
        data-testid="open-preview"
        title={noTarget ? t("publication.previewHint") : undefined}
        aria-describedby={noTarget && hintId ? hintId : undefined}
      >
        <MonitorIcon size={16} />
        {t("project.openPreview")}
      </Link>
    </>
  );
}

/**
 * The project header's action area: one aligned row (the execution controls, then ProjectPrimaryActions), and below the
 * whole row the "publish to get a live URL" hint, a refused Publish (the API's message, role=alert, with the fix's link)
 * and the stored failure of the latest publish.
 */
export function ProjectHeaderActions({
  projectId,
  publication,
  previewTargets,
  isOrgAdmin,
  tasksDone,
  controls,
  publishFn,
}: {
  projectId: string;
  publication: Publication;
  previewTargets: readonly PreviewTarget[] | undefined;
  isOrgAdmin: boolean;
  tasksDone: boolean;
  /** The execution controls, first on the row. */
  controls?: ReactNode;
  publishFn?: (projectId: string) => Promise<unknown>;
}) {
  const { t } = useI18n();
  const [publishError, setPublishError] = useState<unknown>(null);
  const noTarget = previewTargets !== undefined && previewTargets.length === 0;
  const hintId = `open-preview-hint-${projectId}`;
  // A failed publish whose app is still live (an earlier publish) keeps showing its failure too.
  const failed = publication.publish?.state === "failed" && !publishError;
  return (
    <div className="project-actions-area">
      <div className="project-actions" data-testid="project-actions">
        {controls}
        <ProjectPrimaryActions
          projectId={projectId}
          publication={publication}
          previewTargets={previewTargets}
          isOrgAdmin={isOrgAdmin}
          tasksDone={tasksDone}
          onPublished={() => {
            setPublishError(null);
            publication.reload();
          }}
          onPublishError={setPublishError}
          {...(noTarget ? { hintId } : {})}
          {...(publishFn ? { publishFn } : {})}
        />
      </div>
      {noTarget ? (
        <p id={hintId} className="field-hint project-actions-note" data-testid="open-preview-hint">
          {t("publication.previewHint")}
        </p>
      ) : null}
      <PublishRefusal error={publishError} onDismiss={() => setPublishError(null)} />
      {failed ? <PublishFailure publish={publication.publish} /> : null}
    </div>
  );
}

/**
 * What a preview surface shows when the project has no preview target: the live URL when the project is published,
 * else "not published yet" with the Publish action for organization admins.
 */
export function UnpublishedPreview({
  projectId,
  publication,
  isOrgAdmin,
  tasksDone,
  publishFn,
}: {
  projectId: string;
  publication: Publication;
  isOrgAdmin: boolean;
  tasksDone: boolean;
  publishFn?: (projectId: string) => Promise<unknown>;
}) {
  const { t } = useI18n();
  const appUrl = liveAppUrl(publication.publish, undefined);
  if (appUrl) {
    return (
      <EmptyState
        icon={<MonitorIcon />}
        title={t("publication.liveTitle")}
        headingLevel={3}
        actions={
          <a className="btn btn--primary" href={appUrl} target="_blank" rel="noopener noreferrer" data-testid="preview-live-url">
            <ExternalLinkIcon size={16} />
            {t("publication.openApp")}
            <span className="sr-only">{t("common.opensNewTab")}</span>
          </a>
        }
      >
        <p>{t("publication.liveBody")}</p>
        <p className="mono user-content">{appUrl}</p>
      </EmptyState>
    );
  }
  const busy = publishInFlight(publication.publish);
  return (
    <EmptyState
      icon={<MonitorIcon />}
      title={t("publication.emptyTitle")}
      headingLevel={3}
      actions={
        isOrgAdmin ? (
          <div className="stack" style={{ gap: 6, alignItems: "center" }}>
            <PublishButton projectId={projectId} busy={busy} onPublished={publication.reload} testId="preview-publish" {...(publishFn ? { publishFn } : {})} />
            {!tasksDone ? <p className="field-hint">{t("launch.publishBeforeDone")}</p> : null}
          </div>
        ) : null
      }
    >
      <p data-testid="preview-not-published">{busy && publication.publish ? t("publication.publishing", { state: t(`launch.publishState.${publication.publish.state}` as MessageKey) }) : t("publication.emptyBody")}</p>
      <PublishFailure publish={publication.publish} />
      {!isOrgAdmin ? <p className="field-hint">{t("launch.publishNeedsAdmin")}</p> : null}
    </EmptyState>
  );
}
